// dashboard/PatientDetailPanel.js
// Extracted from App.js (2026-08-12 split).

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { c, severityColor, severityBg, severityLabel, conditionLabel, onActivateKeyDown, EVENT_TYPE_KEYS, CHANGE_DOMAIN_KEYS } from './constants';
import { fmtWeight, relDate, getAge, formatPhone, trendDirectionLabel } from './utils';
import { _trackerCache } from './trackerCache';
import { useModalA11y } from './useModalA11y';
import TrendChart from './widgets/TrendChart';
import { buildFindingsNarrative } from './buildFindingsNarrative';
import FinalizePatientModal from './modals/FinalizePatientModal';
import RequestSentModal from './modals/RequestSentModal';
import MuteDelinkModal from './modals/MuteDelinkModal';

// Compare only server-stored answer derivations. vNext's measurement_score
// is preferred; numeric_score is the immutable legacy fallback needed for
// older/test submissions. Both are normalized so higher is better except
// raw_numeric (currently SRR), whose polarity is explicitly inverted.
export function classifyAnswerContribution(currentAnswer, previousAnswer, scoringMode) {
  if (!currentAnswer || !previousAnswer) return null;
  const currentRaw = currentAnswer.measurement_score ?? currentAnswer.numeric_score;
  const previousRaw = previousAnswer.measurement_score ?? previousAnswer.numeric_score;
  if (currentRaw === null || currentRaw === undefined || previousRaw === null || previousRaw === undefined) return null;
  const currentScore = Number(currentRaw);
  const previousScore = Number(previousRaw);
  if (!Number.isFinite(currentScore) || !Number.isFinite(previousScore)) return null;
  const polarity = scoringMode === 'raw_numeric' ? -1 : 1;
  const delta = (currentScore - previousScore) * polarity;
  if (Math.abs(delta) < 0.000001) return null;
  return delta > 0 ? 'positive' : 'negative';
}

export function eventDisplayTitle(label, medicationName) {
  return medicationName ? `${label} — ${medicationName}` : label;
}

export default function PatientDetailPanel({ patient, onClose, onDismissAlert, onReactivateAlert, onPrintSummary, clinicId, profile, vets, recoveryThresholds, onReassignDoctor, useLbs, freezeCutoff, isInCatchUpWindow, canManage, canClinical, onPatientFinalized, onAssignCondition }) {
  const { t } = useTranslation();
  const drawerRef = useModalA11y(onClose);
  const [surveys,       setSurveys]       = useState([]);
  const [loadingSurveys,setLoadingSurveys]= useState(true);
  const [surveysError,  setSurveysError]  = useState('');
  const [surveysRetry,  setSurveysRetry]  = useState(0);
  const [evaluations,   setEvaluations]   = useState(new Map());
  const [vnextRuns,     setVnextRuns]     = useState(new Map());
  // P1.3 (docs/CLINICAL_ENGINE_VNEXT_END_TO_END_AUDIT_2026-10-04.md):
  // overall_trend ("how is your pet doing overall?") is scoring_mode=
  // 'change_only' -- zero score weight, no sentinel rules, deliberately
  // never fed into the engine (Bryan's explicit call, 2026-10-04: declare
  // it context-only rather than wire it into scoring). It was previously
  // buried as one undifferentiated row in the per-check-in answer
  // breakdown; surfaced here instead, same "own section, explicitly
  // unscored" treatment as petChangeLogs below.
  const [ownerReportedTrend, setOwnerReportedTrend] = useState(new Map());
  const [petWeights,    setPetWeights]    = useState([]);
  const [medications,   setMedications]   = useState([]);
  const [seizureLogs,   setSeizureLogs]   = useState([]);
  const [interventionEvents, setInterventionEvents] = useState([]);
  const [petChangeLogs, setPetChangeLogs] = useState([]);
  const [activeMedications, setActiveMedications] = useState([]);
  const [adherenceCounts, setAdherenceCounts] = useState({});
  const [supportingLoadErrors, setSupportingLoadErrors] = useState(() => new Set());
  const [supportingRetry, setSupportingRetry] = useState(0);
  const [actionsOpen,   setActionsOpen]   = useState(false);
  const [actionsSubView, setActionsSubView] = useState(null);
  const [finalizeOpen,   setFinalizeOpen]   = useState(false);
  const [finalizing,     setFinalizing]     = useState(false);
  const [finalizeError,  setFinalizeError]  = useState('');
  const [requestSent,    setRequestSent]    = useState(false);
  const [muteDelinkOpen,   setMuteDelinkOpen]   = useState(null); // 'mute' | 'delink' | null
  const [muteDelinkSaving, setMuteDelinkSaving] = useState(false);
  const [muteDelinkError,  setMuteDelinkError]  = useState('');
  const [unmuting,         setUnmuting]         = useState(false);

  // 2026-08-31, Bryan: a clinic must never be able to unilaterally end
  // monitoring for a pet. This no longer calls finalize_pet_record at
  // all — request_pet_finalization pauses the pet's monitoring and asks
  // the owner to independently pick the real reason and confirm (or
  // dismiss) on their own device. The panel shows an acknowledgment, not
  // a condolences screen — nothing is actually final yet.
  const handleFinalize = async ({ outcomeType, reasonDetail }) => {
    setFinalizing(true);
    setFinalizeError('');
    try {
      const { data: requestId, error } = await supabase.rpc('request_pet_finalization', {
        p_pet_id:                  patient.pet.id,
        p_requested_outcome_type:  outcomeType,
        p_reason_detail:           reasonDetail || null,
      });
      if (error) throw error;
      setFinalizeOpen(false);
      onPatientFinalized?.();
      // Deliberately not awaited — the request already committed above,
      // so a push failure has no bearing on whether the request itself
      // succeeded (same reasoning as notify-enrollment-confirmed).
      // Codex audit finding #4: supabase.functions.invoke resolves
      // {data, error} on a function-level failure rather than rejecting
      // the promise — .catch() alone (the pre-existing notify-enrollment-
      // confirmed pattern this copied) never sees that case, so a real
      // notification failure was silently going unlogged. Checking the
      // resolved error explicitly here; a durable outbox/retry for this
      // notification is still open (Codex's recommendation) if a missed
      // push turns out to matter operationally, since the Home banner
      // fallback only helps once the owner opens the app on their own.
      supabase.functions.invoke('notify-pet-finalization-request', {
        body: { request_id: requestId },
      }).then(({ error: notifyError }) => {
        if (notifyError) console.warn('Owner finalization-request notification failed (non-fatal):', notifyError);
      }).catch(_notifyErr => console.warn('Owner finalization-request notification failed (non-fatal):', _notifyErr));
      setRequestSent(true);
    } catch (err) {
      setFinalizeError(t('patientDetail.errorFinalize'));
    } finally {
      setFinalizing(false);
    }
  };

  // 2026-08-31, phase 2: a clinic's self-service way to stop dealing with
  // a patient without ever touching pets/finalize_pet_record — scoped
  // entirely to pet_clinic_relationships (migration 183). Mute keeps the
  // relationship active and just suppresses this clinic's own dashboard
  // visibility/alerts; de-link actually severs it, requiring the owner to
  // re-link from scratch.
  const handleMuteDelink = async (reason) => {
    setMuteDelinkSaving(true);
    setMuteDelinkError('');
    try {
      const rpcName = muteDelinkOpen === 'delink' ? 'delink_pet_clinic_relationship' : 'mute_pet_clinic_relationship';
      const { error } = await supabase.rpc(rpcName, {
        p_pet_id:    patient.pet.id,
        p_clinic_id: clinicId,
        p_reason:    reason,
      });
      if (error) throw error;
      setMuteDelinkOpen(null);
      onPatientFinalized?.();
      onClose();
    } catch (err) {
      setMuteDelinkError(t('muteDelinkModal.error'));
    } finally {
      setMuteDelinkSaving(false);
    }
  };

  const handleUnmute = async () => {
    setUnmuting(true);
    try {
      const { error } = await supabase.rpc('unmute_pet_clinic_relationship', {
        p_pet_id:    patient.pet.id,
        p_clinic_id: clinicId,
      });
      if (error) throw error;
      onPatientFinalized?.();
      onClose();
    } catch (err) {
      // Was console.warn only -- unlike every sibling quick action here
      // (handleMuteDelink's own modal error, App.js's Dismiss/Reactivate/
      // Reassign window.alert()s), a failed unmute gave zero visible
      // feedback: the menu item just reverted from "Saving..." back to
      // "Unmute" with nothing telling the Doctor it didn't work.
      console.warn('Unmute failed:', err);
      window.alert(t('mainView.unmuteFailed'));
    } finally {
      setUnmuting(false);
    }
  };
  const [expandedId,    setExpandedId]    = useState(null);
  const [answerCache,   setAnswerCache]   = useState({});
  const [medSnapshotCache, setMedSnapshotCache] = useState({});
  const [loadingAns,    setLoadingAns]    = useState(false);
  const [answerLoadErrors, setAnswerLoadErrors] = useState(() => new Set());
  const actionsRef = useRef(null);

  const markSupportingLoadError = useCallback((section, error) => {
    console.warn(`Failed to load patient ${section}:`, error?.message || error);
    setSupportingLoadErrors(previous => {
      const next = new Set(previous);
      next.add(section);
      return next;
    });
  }, []);

  useEffect(() => {
    const h = e => { if (actionsRef.current && !actionsRef.current.contains(e.target)) { setActionsOpen(false); setActionsSubView(null); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // The drawer is modal: prevent the page beneath it from scrolling while
  // preserving the prior value in case another part of the host page set it.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoadingSurveys(true);
      setSurveysError('');
      let q = supabase
        .from('survey_submissions')
        .select('id, clinical_score, clinical_score_kind, clinical_score_unit, clinical_score_delta, clinical_score_algorithm_version, submitted_at, due_at, template_id, owner_note, severity')
        .eq('pet_id', patient.pet.id)
        .eq('condition_id', patient.condition?.id)
        .eq('status', 'completed')
        .order('submitted_at', { ascending: false })
        .limit(8);
      // freezeCutoff (past-due grace period elapsed) hides survey completions
      // after the freeze instant — previously collected data stays visible.
      if (freezeCutoff) q = q.lte('submitted_at', freezeCutoff.toISOString());
      const { data, error } = await q;
      if (error) {
        console.warn('Failed to load patient survey history:', error.message);
        setSurveys([]);
        setEvaluations(new Map());
        setSurveysError(error.message);
        setLoadingSurveys(false);
        return;
      }
      setSurveys(data || []);
      setLoadingSurveys(false);

      // Phase 7: survey_evaluations (severity_band/change_state/reason
      // code/sentinel findings) had no UI representation anywhere before
      // this — fetched here, keyed by survey_submission_id, so
      // TrendChart's dots can show WHY a point is the color it is on
      // hover instead of just the color itself. Only the surveys this
      // panel is actually charting need it, so this fetch waits for the
      // survey ids above rather than a separate condition-scoped query.
      const ids = (data || []).map(s => s.id);
      if (ids.length > 0) {
        const { data: evalData, error: evalError } = await supabase
          .from('survey_evaluations')
          .select('survey_submission_id, severity_band, change_state, primary_reason_code, reasons')
          .in('survey_submission_id', ids);
        if (evalError) {
          // Evaluation annotations enrich the chart but are not required to
          // show the underlying survey history. Fail this portion open while
          // retaining an observable diagnostic instead of hiding the chart.
          console.warn('Failed to load survey evaluation annotations:', evalError.message);
          setEvaluations(new Map());
        } else {
          setEvaluations(new Map((evalData || []).map(e => [e.survey_submission_id, e])));
        }

        // vNext findings-based "why" narrative (release-blocker #2) --
        // survey_evaluation_runs is the vNext shadow engine's append-only
        // history (Stage 0A, migration 260), separate from survey_
        // evaluations above (the older, real engine's own record). Same
        // best-effort treatment: a failed/empty read here degrades to the
        // existing clinicalSummary/alert.message narrative, never blocks
        // the rest of the panel.
        const { data: runsData, error: runsError } = await supabase
          .from('survey_evaluation_runs')
          .select('survey_submission_id, findings, clusters')
          .in('survey_submission_id', ids)
          .eq('computation_kind', 'original');
        if (runsError) {
          console.warn('Failed to load vNext findings:', runsError.message);
          setVnextRuns(new Map());
        } else {
          setVnextRuns(new Map((runsData || []).map(r => [r.survey_submission_id, r])));
        }

        // P1.3: owner's self-reported overall-trend answer, per check-in.
        // Best-effort/fail-open, same posture as the evaluations/vNext
        // fetches above -- this is a supplementary note, never required
        // to show the rest of the panel.
        const { data: trendData, error: trendError } = await supabase
          .from('survey_answers')
          .select('survey_submission_id, answer_value')
          .eq('question_key', 'overall_trend')
          .in('survey_submission_id', ids);
        if (trendError) {
          console.warn('Failed to load owner-reported trend answers:', trendError.message);
          setOwnerReportedTrend(new Map());
        } else {
          setOwnerReportedTrend(new Map((trendData || []).map(r => [r.survey_submission_id, r.answer_value])));
        }
      } else {
        setEvaluations(new Map());
        setVnextRuns(new Map());
        setOwnerReportedTrend(new Map());
      }
    };
    if (patient.condition?.id) fetch();
  }, [patient.pet.id, patient.condition?.id, freezeCutoff, surveysRetry]);

  useEffect(() => {
    const petId = patient.pet.id;
    // Skip the cache while frozen (past-due grace period elapsed) — it isn't
    // freeze-aware, and this is a rare enough state that always fetching
    // fresh is worth the correctness (an already-open panel must reflect a
    // just-restored subscription immediately, not a stale pre-freeze cache).
    if (!freezeCutoff && _trackerCache[petId]?.weights) { setPetWeights(_trackerCache[petId].weights); return; }
    const threeMonthsAgo = new Date(); threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    let q = supabase
      .from('weight_entries')
      .select('weight, unit, measured_at')
      .eq('pet_id', petId)
      .gte('measured_at', threeMonthsAgo.toISOString().split('T')[0])
      .is('deleted_at', null)
      .order('measured_at', { ascending: true })
      .limit(10);
    if (freezeCutoff) q = q.lte('measured_at', freezeCutoff.toISOString());
    q.then(({ data, error }) => {
      if (error) { markSupportingLoadError('weights', error); return; }
      const w = data || [];
      if (!freezeCutoff) _trackerCache[petId] = { ..._trackerCache[petId], weights: w };
      setPetWeights(w);
    }).catch(error => markSupportingLoadError('weights', error));
  }, [patient.pet.id, freezeCutoff, supportingRetry, markSupportingLoadError]);

  useEffect(() => {
    const petId = patient.pet.id;
    if (!freezeCutoff && _trackerCache[petId]?.medications) {
      setMedications(_trackerCache[petId].medications);
      setSeizureLogs(_trackerCache[petId].seizureLogs || []);
      return;
    }
    const since12m = new Date(); since12m.setFullYear(since12m.getFullYear() - 1);
    // medication_entries is structural (current med list), not filtered.
    // seizure_logs is an event stream — freezeCutoff hides entries logged
    // after the freeze instant.
    let szQ = supabase.from('seizure_logs')
      .select('occurred_at')
      .eq('pet_id', petId)
      .gte('occurred_at', since12m.toISOString())
      .is('deleted_at', null)
      .order('occurred_at');
    if (freezeCutoff) szQ = szQ.lte('occurred_at', freezeCutoff.toISOString());
    Promise.all([
      // Feeds TrendChart's medication-start annotations, which only ever
      // need to cover the chart's own visible window (bounded by the
      // surveys prop's date range, itself already capped elsewhere) — 20
      // most-recent start dates comfortably covers that, consistent with
      // every other tracker query in this component capping results.
      supabase.from('medication_entries')
        .select('medication_name, start_date')
        .eq('pet_id', petId)
        .not('start_date', 'is', null)
        .order('start_date', { ascending: false })
        .limit(20),
      szQ,
    ]).then(([medRes, szRes]) => {
      if (medRes.error || szRes.error) {
        markSupportingLoadError('trend context', medRes.error || szRes.error);
        return;
      }
      const meds = medRes.data || [];
      const sz   = szRes.data  || [];
      if (!freezeCutoff) _trackerCache[petId] = { ..._trackerCache[petId], medications: meds, seizureLogs: sz };
      setMedications(meds);
      setSeizureLogs(sz);
    }).catch(error => markSupportingLoadError('trend context', error));
  }, [patient.pet.id, freezeCutoff, supportingRetry, markSupportingLoadError]);

  useEffect(() => {
    const petId = patient.pet.id;
    if (!freezeCutoff && _trackerCache[petId]?.interventionEvents) {
      setInterventionEvents(_trackerCache[petId].interventionEvents);
      return;
    }
    let q = supabase
      .from('intervention_events')
      .select('event_type, event_date, source, notes')
      .eq('pet_id', petId)
      .is('deleted_at', null)
      .order('event_date', { ascending: false })
      .limit(10);
    if (freezeCutoff) q = q.lte('event_date', freezeCutoff.toISOString());
    // Merged in here rather than a separate section — medication_change_reasons
    // (2026-07-24) now carries what medication_started/medication_stopped/
    // dose_changed used to be as manual intervention_events entries; same
    // display shape, same EVENT_TYPE_KEYS labels, so this reuses the
    // existing Recent Events rendering unchanged rather than needing a
    // third dashboard section for what's conceptually the same kind of
    // information (a discrete point-in-time event).
    let mq = supabase
      .from('medication_change_reasons')
      .select('change_type, recorded_at, reason, medication_entry:medication_entries!medication_entry_id(medication_name)')
      .eq('pet_id', petId)
      .is('deleted_at', null)
      .order('recorded_at', { ascending: false })
      .limit(10);
    if (freezeCutoff) mq = mq.lte('recorded_at', freezeCutoff.toISOString());
    Promise.all([q, mq]).then(([eventResult, medChangeResult]) => {
      if (eventResult.error || medChangeResult.error) {
        markSupportingLoadError('recent events', eventResult.error || medChangeResult.error);
        return;
      }
      const events = eventResult.data;
      const medChanges = medChangeResult.data;
      const merged = [
        ...(events || []),
        ...(medChanges || []).map(m => ({
          event_type: m.change_type,
          event_date: (m.recorded_at || '').slice(0, 10),
          source:     'owner',
          notes:      m.reason,
          medication_name: m.medication_entry?.medication_name || null,
        })),
      ]
        .sort((a, b) => (a.event_date < b.event_date ? 1 : a.event_date > b.event_date ? -1 : 0))
        .slice(0, 10);
      if (!freezeCutoff) _trackerCache[petId] = { ..._trackerCache[petId], interventionEvents: merged };
      setInterventionEvents(merged);
    }).catch(error => markSupportingLoadError('recent events', error));
  }, [patient.pet.id, freezeCutoff, supportingRetry, markSupportingLoadError]);

  // pet_change_logs ("Log Change" button, 2026-07-24) — owner-perceived
  // domain deviations (energy, appetite, etc.), purely informational, kept
  // in its own state/section rather than merged into interventionEvents
  // above since they're a different kind of information (a discrete
  // clinical-ish event vs. an owner's ongoing-domain observation) — same
  // fetch/cache shape as intervention_events otherwise.
  useEffect(() => {
    const petId = patient.pet.id;
    if (!freezeCutoff && _trackerCache[petId]?.petChangeLogs) {
      setPetChangeLogs(_trackerCache[petId].petChangeLogs);
      return;
    }
    let q = supabase
      .from('pet_change_logs')
      .select('domain, log_date, note')
      .eq('pet_id', petId)
      .is('deleted_at', null)
      .order('log_date', { ascending: false })
      .limit(10);
    if (freezeCutoff) q = q.lte('log_date', freezeCutoff.toISOString());
    q.then(({ data, error }) => {
      if (error) { markSupportingLoadError('owner changes', error); return; }
      const changes = data || [];
      if (!freezeCutoff) _trackerCache[petId] = { ..._trackerCache[petId], petChangeLogs: changes };
      setPetChangeLogs(changes);
    }).catch(error => markSupportingLoadError('owner changes', error));
  }, [patient.pet.id, freezeCutoff, supportingRetry, markSupportingLoadError]);

  useEffect(() => {
    const petId = patient.pet.id;
    if (!freezeCutoff && _trackerCache[petId]?.activeMedications) {
      setActiveMedications(_trackerCache[petId].activeMedications);
      setAdherenceCounts(_trackerCache[petId].adherenceCounts || {});
      return;
    }
    const since30d = new Date(); since30d.setDate(since30d.getDate() - 30);
    // adherence_entries — written only from the mobile app's "Mark as
    // Given" push action, previously collected but never read anywhere,
    // including here. freezeCutoff hides confirmations recorded after the
    // freeze instant; medication_entries itself is structural (current
    // active med list), not filtered.
    let adhQ = supabase.from('adherence_entries')
      .select('medication_entry_id')
      .eq('pet_id', petId)
      .gte('recorded_at', since30d.toISOString())
      .is('deleted_at', null);
    if (freezeCutoff) adhQ = adhQ.lte('recorded_at', freezeCutoff.toISOString());
    Promise.all([
      supabase.from('medication_entries')
        .select('id, medication_name, dose, dose_unit, frequency')
        .eq('pet_id', petId)
        .eq('is_active', true)
        .limit(20),
      adhQ,
    ]).then(([medsRes, adhRes]) => {
      if (medsRes.error || adhRes.error) {
        markSupportingLoadError('medications', medsRes.error || adhRes.error);
        return;
      }
      const meds = medsRes.data || [];
      const counts = {};
      (adhRes.data || []).forEach(a => { counts[a.medication_entry_id] = (counts[a.medication_entry_id] || 0) + 1; });
      if (!freezeCutoff) _trackerCache[petId] = { ..._trackerCache[petId], activeMedications: meds, adherenceCounts: counts };
      setActiveMedications(meds);
      setAdherenceCounts(counts);
    }).catch(error => markSupportingLoadError('medications', error));
  }, [patient.pet.id, freezeCutoff, supportingRetry, markSupportingLoadError]);

  const weightArrow = () => {
    if (petWeights.length < 2) return null;
    const pct   = ((parseFloat(petWeights[petWeights.length-1].weight) - parseFloat(petWeights[0].weight)) / parseFloat(petWeights[0].weight)) * 100;
    const arrow = pct > 5 ? '↗' : pct < -5 ? '↘' : '→';
    // Reuses trackers.weight_trend_* — exact same three words already
    // translated there for the weight tracker card.
    const trend = pct > 5 ? t('trackers.weight_trend_gaining') : pct < -5 ? t('trackers.weight_trend_losing') : t('trackers.weight_trend_stable');
    return { arrow, trend, pct };
  };

  function formatAnswerValue(answer_value, answer_type) {
    if (answer_value == null || answer_value === '') return '—';
    switch (answer_type) {
      case 'yes_no':
        return (answer_value === 'true' || answer_value === true || answer_value === 'Yes') ? t('patientDetail.answerYes') : t('patientDetail.answerNo');
      case 'scale_1_5':
        return t('patientDetail.scaleOutOf5', { value: answer_value });
      case 'multi_select':
        try { const a = JSON.parse(answer_value); return Array.isArray(a) ? a.join(', ') : answer_value; }
        catch { return answer_value; }
      default:
        return String(answer_value);
    }
  }

  async function loadAnswers(submissionId, templateId) {
    if (answerCache[submissionId] !== undefined) return;
    setLoadingAns(true);
    try {
      // survey_medication_snapshots — an immutable record of the active med
      // list at the moment this survey was submitted, written by
      // SurveyScreen.js specifically so a later reviewer can correlate a
      // score change with a medication change. Previously written and never
      // read anywhere — surfaced here since expanding a past check-in is
      // exactly the moment a clinician is already looking for that context.
      const submissionIndex = surveys.findIndex(s => s.id === submissionId);
      const previousSubmissionId = submissionIndex >= 0 ? surveys[submissionIndex + 1]?.id : null;
      const answerSubmissionIds = [submissionId, previousSubmissionId].filter(Boolean);
      const [answersRes, questionsRes, medSnapshotRes] = await Promise.all([
        supabase.from('survey_answers')
          .select('survey_submission_id, question_key, answer_value, numeric_score, measurement_score')
          .in('survey_submission_id', answerSubmissionIds),
        supabase.from('survey_questions').select('question_key, question_text, answer_type, display_order, score_weight, scoring_mode').eq('template_id', templateId).eq('is_active', true).order('display_order'),
        supabase.from('survey_medication_snapshots').select('medication_name, dose, dose_unit, frequency, exposure_status').eq('survey_submission_id', submissionId),
      ]);
      // supabase-js resolves rather than rejects on a PostgREST-level
      // failure, so an unchecked `error` here previously fell through to
      // `answers || []` and got permanently cached as [] — indistinguishable
      // from a real completed submission with zero answers (never actually
      // possible) and, worse, un-retryable once cached (see the `answerCache
      // !== undefined` guard above).
      if (answersRes.error || questionsRes.error || medSnapshotRes.error) {
        throw answersRes.error || questionsRes.error || medSnapshotRes.error;
      }
      const qMap   = Object.fromEntries((questionsRes.data || []).map(q => [q.question_key, q]));
      const previousByKey = Object.fromEntries((answersRes.data || [])
        .filter(a => a.survey_submission_id === previousSubmissionId)
        .map(a => [a.question_key, a]));
      const merged = (answersRes.data || [])
        .filter(a => a.survey_submission_id === submissionId)
        .map(a => {
          const question = qMap[a.question_key] || { question_text: a.question_key, answer_type: 'text', display_order: 99, score_weight: 1 };
          return {
            ...a,
            ...question,
            contribution: Number(question.score_weight) === 0
              ? null
              : classifyAnswerContribution(a, previousByKey[a.question_key], question.scoring_mode),
          };
        })
        .sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99));
      setAnswerCache(prev => ({ ...prev, [submissionId]: merged }));
      setMedSnapshotCache(prev => ({ ...prev, [submissionId]: medSnapshotRes.data || [] }));
      setAnswerLoadErrors(prev => {
        if (!prev.has(submissionId)) return prev;
        const next = new Set(prev);
        next.delete(submissionId);
        return next;
      });
    } catch (e) {
      console.warn('Failed to load answers:', e);
      // Deliberately does NOT populate answerCache/medSnapshotCache — that
      // would permanently short-circuit the `answerCache[submissionId] !==
      // undefined` guard in handleRowClick and make this un-retryable.
      setAnswerLoadErrors(prev => new Set(prev).add(submissionId));
    } finally {
      setLoadingAns(false);
    }
  }

  function handleRowClick(submissionId, templateId) {
    if (expandedId === submissionId) { setExpandedId(null); return; }
    setExpandedId(submissionId);
    loadAnswers(submissionId, templateId);
  }

  const sev   = patient.severity;
  const color = severityColor(sev);
  // #9: same lookup ConditionRow/StatusNarrative use — see App.js's
  // fetchRecoveryThresholds comment for why this is safe to fetch globally.
  const recoveryThreshold = recoveryThresholds?.[patient.condition?.condition_type]?.[sev];

  return (
    <div
      ref={drawerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="patient-detail-title"
      tabIndex={-1}
      style={{ position: 'fixed', inset: 0, zIndex: 40, fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div
        data-testid="patient-detail-backdrop"
        aria-hidden="true"
        onMouseDown={onClose}
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(31, 47, 56, 0.28)' }}
      />
      <div className="patient-detail-drawer" style={{
        position: 'absolute',
        top: 0, right: 0, bottom: 0,
        width: 'min(560px, 100vw)',
        backgroundColor: c.white,
        borderLeft: `1px solid ${c.border}`,
        boxShadow: '-8px 0 32px rgba(31,47,56,0.2)',
        overflowY: 'auto',
      }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: `1px solid ${c.border}`,
        position: 'sticky',
        top: 0,
        backgroundColor: c.white,
        zIndex: 2,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 26, flexShrink: 0,
              backgroundColor: '#e8eae6', overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {patient.pet?.photo_url ? (
                <img src={patient.pet.photo_url} alt={patient.pet?.name}
                  style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 26 }}
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <span style={{ fontSize: 22 }}>🐾</span>
              )}
            </div>
            <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <span id="patient-detail-title" style={{ fontSize: 18, fontWeight: 700, color: c.slate }}>{patient.pet?.name}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, color,
                backgroundColor: severityBg(sev),
                padding: '3px 9px', borderRadius: 10,
                border: `1px solid ${color}30`,
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {severityLabel(sev)}
              </span>
            </div>
            <div style={{ fontSize: 12, color: c.textSoft }}>
              {getAge(patient.pet?.date_of_birth)}{patient.pet?.species ? ` ${patient.pet.species}` : ''}
              {patient.pet?.breed ? ` · ${patient.pet.breed}` : ''}
            </div>
            <div style={{ fontSize: 12, color: c.textSoft, marginTop: 2 }}>
              {conditionLabel(patient.condition?.condition_type)} {t('patientDetail.monitoring')}
            </div>
            {patient.enrolledAt && (
              <div style={{ fontSize: 12, color: c.textSoft, marginTop: 2 }}>
                {t('patientDetail.enrolled', { date: new Date(patient.enrolledAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }) })}
              </div>
            )}
          </div>{/* end text block */}
          </div>{/* end avatar + text flex */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Actions dropdown */}
            <div ref={actionsRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setActionsOpen(o => !o)}
                style={{ padding: '6px 12px', border: `1px solid ${c.border}`, borderRadius: 8, background: 'none', fontSize: 12, color: c.textSoft, cursor: 'pointer' }}
              >
                {t('patientDetail.actionsMenu')}
              </button>
              {actionsOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 4,
                  backgroundColor: c.white, border: `1px solid ${c.border}`,
                  borderRadius: 10, boxShadow: '0 4px 16px rgba(79,100,115,0.14)',
                  zIndex: 100, minWidth: 180, overflow: 'hidden',
                }}>
                  {actionsSubView === 'assignDoctor' ? (
                    <>
                      <button onClick={() => setActionsSubView(null)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                        padding: '10px 14px', textAlign: 'left', background: c.fog,
                        border: 'none', borderBottom: `1px solid ${c.border}`,
                        fontSize: 12, color: c.textSoft, cursor: 'pointer',
                      }}>{t('patientDetail.back')}</button>
                      {vets.length === 0 && (
                        <div style={{ padding: '10px 14px', fontSize: 12, color: c.textSoft }}>{t('patientDetail.noDoctorsOnFile')}</div>
                      )}
                      {vets.map(v => {
                        const isCurrent = patient.assignedVet?.id === v.id;
                        return (
                          <button key={v.id} onClick={() => { onReassignDoctor(patient.condition?.id, v.id); setActionsOpen(false); setActionsSubView(null); }} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            width: '100%', padding: '10px 14px', textAlign: 'left',
                            background: isCurrent ? c.fog : 'none', border: 'none',
                            fontSize: 13, color: c.slate, cursor: 'pointer',
                          }}>
                            <span>{t('patientDetail.doctorPrefix', { firstName: v.first_name, lastName: v.last_name })}</span>
                            {isCurrent && <span style={{ fontSize: 11, color: c.moss }}>✓</span>}
                          </button>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      {[
                        { label: t('patientDetail.print'),   action: () => onPrintSummary(patient) },
                        { divider: true },
                        { label: t('patientDetail.assignDoctor'), action: () => setActionsSubView('assignDoctor'), noClose: true },
                        // Reuses the same AssignModal/assign_workflow_to_pet
                        // RPC the pending-enrollment "Confirm" button already
                        // uses — that was the ONLY place in the whole app that
                        // could open it, so an already-active pet had no way
                        // to pick up a second condition from the dashboard at
                        // all (2026-08-05; will also live in the Workflows
                        // menu once that ships, per Bryan). Gated on
                        // canClinical (doctor/admin only), matching the
                        // pending-enrollment Confirm button's own gate — NOT
                        // canManage, which includes is_manager. Manager
                        // accounts are deliberately excluded from patient/
                        // clinical data (free to create, billing/admin scope
                        // only per Bryan, 2026-08-06) — assigning a condition
                        // to a pet is exactly the kind of clinical action
                        // that boundary is for.
                        ...(canClinical ? [
                          { label: t('patientDetail.assignCondition'), action: () => onAssignCondition(patient) },
                        ] : []),
                        // Gated on canClinical (any active Doctor at this
                        // clinic), not canFinalize — mute_pet_clinic_
                        // relationship/delink authorize any active Doctor
                        // for the relationship's clinic, not just the vet
                        // assigned to this specific condition. Deliberately
                        // NOT reusing canFinalize's Manager-inclusive gate
                        // here (unlike the known, accepted Finalize
                        // dead-end below) — no reason to show a pure
                        // Manager an action the RPC will always reject.
                        ...(canClinical ? [
                          { divider: true },
                          // manuallyMuted, not muted — an auto-derived
                          // (phase 3) inactivity classification has
                          // nothing to "unmute": it clears itself the
                          // moment the owner is active again.
                          patient.manuallyMuted
                            ? { label: unmuting ? t('muteDelinkModal.saving') : t('patientDetail.unmutePatientMenu'), action: unmuting ? undefined : handleUnmute }
                            : { label: t('patientDetail.mutePatientMenu'), action: () => setMuteDelinkOpen('mute') },
                          { label: t('patientDetail.delinkPatientMenu'), action: () => setMuteDelinkOpen('delink'), danger: true },
                        ] : []),
                        // Codex audit finding #7: this used to gate on
                        // canManage || isAssignedVet, but request_pet_
                        // finalization's RPC actually authorizes any
                        // active Doctor at the clinic (is_active_clinic_
                        // doctor_for_pet) — a pure Manager saw the action
                        // and always got a server-side authorization
                        // error. Matches the same canClinical gate the
                        // mute/de-link actions above already use.
                        ...(canClinical ? [
                          { divider: true },
                          { label: t('patientDetail.finalizePatientMenu'), action: () => setFinalizeOpen(true), danger: true },
                        ] : []),
                      ].map((a, i) => a.divider ? (
                        <div key={i} style={{ height: 1, backgroundColor: c.border }} />
                      ) : (
                        <button key={i} onClick={() => { a.action(); if (!a.noClose) setActionsOpen(false); }} style={{
                          display: 'block', width: '100%', padding: '10px 14px',
                          textAlign: 'left', background: 'none', border: 'none',
                          fontSize: 13, color: a.danger ? c.red : c.slate, cursor: 'pointer',
                        }}>{a.label}</button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            <button onClick={onClose} aria-label={t('patientDetail.closeDetailsA11y')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: c.textSoft, padding: 4 }}>×</button>
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 20px' }}>

        {/* Companion SRR alerting while this CHF survey itself still reads
            stable — CHF and SRR stay separate condition rows/trend charts
            on purpose, but this cross-reference makes sure a clinician
            reviewing CHF specifically doesn't miss a concerning breathing-
            rate reading just because it lives on a different card. */}
        {patient.linkedSignificantChange && (
          <div style={{
            backgroundColor: c.redLight, border: `1px solid ${c.red}30`,
            borderRadius: 10, padding: 14, marginBottom: 16,
          }}>
            {/* c.sandstone previously failed WCAG AA contrast here too
                (~2.05:1, needs 4.5:1) — same fix as the row-level badge. */}
            <div style={{ fontSize: 11, fontWeight: 700, color: c.red, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              {t('patientDetail.companionAlertTitle')}
            </div>
            <p style={{ fontSize: 13, color: c.slate, margin: 0, lineHeight: 1.5 }}>{patient.linkedSignificantChange.message}</p>
          </div>
        )}

        {/* Alert */}
        {patient.alert && (
          <div style={{
            backgroundColor: severityBg(sev),
            border: `1px solid ${color}30`,
            borderRadius: 10, padding: 14, marginBottom: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {t('patientDetail.activeAlert')}
              {isInCatchUpWindow?.(patient.alert.created_at) && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: c.slate, backgroundColor: c.fog,
                  border: `1px solid ${c.border}`, borderRadius: 10, padding: '2px 8px',
                  textTransform: 'none', letterSpacing: 0,
                }}>
                  {t('patientDetail.catchUpBadge')}
                </span>
              )}
              {/* current_severity (migration 195/196): the latest single
                  check-in's own result, independent of the follow-up tier
                  above (sev/patient.severity). Shown only when it differs
                  from the follow-up tier — that's exactly the case this
                  whole redesign exists for (e.g. a Red follow-up with a
                  recent Yellow or Green check-in still recovering toward
                  step-down). Never shown as competing full-card color, per
                  the accepted design — a small secondary badge only. */}
              {patient.alert.current_severity && patient.alert.current_severity !== sev && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: severityColor(patient.alert.current_severity),
                  backgroundColor: severityBg(patient.alert.current_severity),
                  border: `1px solid ${severityColor(patient.alert.current_severity)}30`, borderRadius: 10, padding: '2px 8px',
                  textTransform: 'none', letterSpacing: 0,
                }}>
                  {t('patientDetail.latestCheckIn')}: {severityLabel(patient.alert.current_severity)}
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: c.slate, margin: '0 0 12px', lineHeight: 1.5 }}>{patient.alert.message}</p>
            {/* #9 (2026-09-09) — see StatusNarrative.js's matching comment
                for the roster-card version of this same display. */}
            {sev !== 'green' && patient.alert.recovery_streak > 0 && recoveryThreshold != null && (
              <p style={{ fontSize: 12, color: c.textSoft, margin: '0 0 12px' }}>
                {sev === 'immediate_review' && patient.alert.recovery_streak >= recoveryThreshold
                  ? t('patientDetail.recoveryReadyForReview')
                  : t('patientDetail.recoveryProgress', { streak: patient.alert.recovery_streak, threshold: recoveryThreshold })}
              </p>
            )}
            {/* Mark Reviewed / Make Active Again — merged 2026-09-09
                (Bryan): Dismiss, Snooze, and Acknowledge were three
                buttons doing the same underlying thing (move this out of
                the active queue) with only cosmetic differences in when
                it resurfaces. One button now: reviewing is a
                queue-position change only, never a severity/color change
                (see docs/ALERT_LIFECYCLE_REDESIGN) — except for the one
                genuine side effect below, carried over from Acknowledge.
                Explanatory caption sits above the action so a clinician
                knows what it does *before* clicking, not just after —
                Acknowledge's old "no feedback" confusion (2026-09-08)
                taught us not to rely on post-click state alone. */}
            <p style={{ fontSize: 11, color: c.textSoft, margin: '0 0 8px', lineHeight: 1.4 }}>
              {patient.alert.dismissed_at
                ? t('patientDetail.dismissExplainedActive', { time: relDate(patient.alert.dismissed_at) })
                : t('patientDetail.dismissExplained')}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {patient.alert.dismissed_at ? (
                <button onClick={() => onReactivateAlert(patient.alert.id)} style={{ padding: '6px 14px', backgroundColor: color, color: c.white, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {t('patientDetail.makeActiveAgain')}
                </button>
              ) : (
                <button onClick={() => onDismissAlert(patient.alert.id)} style={{ padding: '6px 14px', backgroundColor: color, color: c.white, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {t('patientDetail.markReviewed')}
                </button>
              )}
            </div>
          </div>
        )}

        {supportingLoadErrors.size > 0 && (
          <div role="alert" style={{ backgroundColor: '#FFF8EB', border: `1px solid ${c.sandstoneText}45`, borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: c.slate, lineHeight: 1.45, marginBottom: 8 }}>
              {t('patientDetail.supportingDataLoadError')}
            </div>
            <button
              onClick={() => {
                setSupportingLoadErrors(new Set());
                setSupportingRetry(n => n + 1);
              }}
              style={{ padding: '5px 10px', backgroundColor: c.white, color: c.slate, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* At a Glance — release-blocker #1 (docs/BUILD_STATE.md,
            2026-09-27, Bryan): no raw numeric score anywhere client-facing.
            "Last score"/"Last change" used to render surveys[0].
            clinical_score and its signed delta directly; replaced with a
            qualitative trend word derived from the delta's sign only. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: t('patientDetail.statSurveys'), value: surveysError ? '—' : surveys.length },
            { label: t('patientDetail.statTrend'), value: trendDirectionLabel(surveys[0]?.clinical_score_delta, t) },
          ].map(s => (
            <div key={s.label} style={{ backgroundColor: c.fog, borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.slate }}>{s.value}</div>
              <div style={{ fontSize: 11, color: c.textSoft, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* "Why" narrative (release-blocker #2) — the latest completed
            survey's vNext findings/clusters, composed into up to 3
            plain-English lines naming the specific things that changed,
            instead of just a severity-band label. Falls back to the
            condition's clinicalSummary when vNext hasn't evaluated this
            submission (most conditions/pets, until vNext is live-tested
            at real scale). */}
        {surveys[0] && (() => {
          const latestRun = vnextRuns.get(surveys[0].id);
          const lines = buildFindingsNarrative(latestRun?.findings, latestRun?.clusters);
          const text = lines.length > 0 ? lines : (patient.condition?.clinical_summary ? [patient.condition.clinical_summary] : []);
          return text.length > 0 ? (
            <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('patientDetail.statusSummary')}</div>
              {text.map((line, i) => (
                <div key={i} style={{ fontSize: 13, color: sev === 'red' ? c.red : c.slate, fontWeight: 500, marginBottom: i < text.length - 1 ? 4 : 0 }}>{line}</div>
              ))}
            </div>
          ) : null;
        })()}

        {/* Owner-reported trend (P1.3) -- deliberately its own note, never
            merged into the "why" card above: that card is the engine's
            scoring-driven narrative, this is an owner's unscored self-
            report, and the label says so explicitly rather than implying
            it influenced the result above it. */}
        {surveys[0] && ownerReportedTrend.get(surveys[0].id) && (
          <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t('patientDetail.ownerReportedTrend')}</div>
            <div style={{ fontSize: 13, color: c.slate, fontWeight: 500 }}>{ownerReportedTrend.get(surveys[0].id)}</div>
          </div>
        )}

        {/* Score trend sparkline */}
        {!loadingSurveys && !surveysError && <TrendChart surveys={surveys} medications={medications} seizures={seizureLogs} sev={sev} evaluations={evaluations} vnextRuns={vnextRuns} />}

        {/* Weight trend (if data exists) */}
        {petWeights.length > 0 && (() => {
          const wt    = weightArrow();
          const last3 = [...petWeights].reverse().slice(0, 3);
          return (
            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, border: `1px solid ${c.border}`, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('patientDetail.weight3mo')}</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  {wt && (
                    <div style={{ textAlign: 'center', flexShrink: 0, marginTop: 2 }}>
                      <div style={{ fontSize: 22, lineHeight: 1, color: c.slate }}>{wt.arrow}</div>
                      <div style={{ fontSize: 9, color: c.textSoft, marginTop: 3 }}>{wt.trend}</div>
                    </div>
                  )}
                  <div>
                    {last3.map((w, i) => {
                      const d = new Date(w.measured_at + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
                      return (
                        <div key={i} style={{ fontSize: i === 0 ? 13 : 12, fontWeight: i === 0 ? 600 : 400, color: i === 0 ? c.slate : c.textSoft, marginBottom: 2 }}>
                          {fmtWeight(w.weight, useLbs)} <span style={{ fontSize: 10, color: c.textSoft }}>{d}</span>
                        </div>
                      );
                    })}
                    {wt && <div style={{ fontSize: 10, color: c.textSoft, marginTop: 3 }}>{wt.pct >= 0 ? '+' : ''}{wt.pct.toFixed(1)}%</div>}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Medications — adherence_entries (written only from the mobile
            app's "Mark as Given" push action) was previously collected but
            never read anywhere at all, including here. Most medications
            won't show a confirmed count, since it depends on the owner
            tapping that specific notification action rather than logging
            in-app generally — shown when present, omitted otherwise. */}
        {activeMedications.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('patientDetail.medications')}</div>
            <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${c.border}` }}>
              {activeMedications.map((m, i) => {
                const dose = [m.dose, m.dose_unit].filter(Boolean).join(' ');
                const confirmedCount = adherenceCounts[m.id];
                return (
                  <div key={m.id} style={{
                    display: 'flex', alignItems: 'center', padding: '9px 12px',
                    borderBottom: i < activeMedications.length - 1 ? `1px solid ${c.border}` : 'none',
                  }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12, color: c.slate, fontWeight: 600 }}>{m.medication_name}</span>
                      {(dose || m.frequency) && (
                        <span style={{ fontSize: 11, color: c.textSoft, marginLeft: 6 }}>
                          {[dose, m.frequency].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    {confirmedCount ? (
                      <span style={{ fontSize: 11, color: c.moss }}>{t('patientDetail.confirmedCount', { count: confirmedCount })}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent events — hospitalization/ER/surgery/medication changes/
            other discrete clinical events. Auto-populated from the
            "unexpected vet visit" survey question on every template
            (process-survey-alert), plus (2026-07-24) the owner-facing "Log
            Event" button on PetHomeScreen — both write to the same table. */}
        {interventionEvents.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('patientDetail.recentEvents')}</div>
            <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${c.border}` }}>
              {interventionEvents.map((ev, i) => {
                // Reuses the module-level EVENT_TYPE_KEYS Set (previously a
                // separate, narrower local array here that had silently
                // drifted out of sync with the module-level one buildEventsWidget
                // uses — fixed 2026-07-24 while adding the new Log Event
                // categories, so both places can't drift again).
                const label = EVENT_TYPE_KEYS.has(ev.event_type)
                  ? t(`eventType.${ev.event_type}`)
                  : ev.event_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || t('eventType.fallback');
                const title = eventDisplayTitle(label, ev.medication_name);
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', padding: '9px 12px',
                    borderBottom: i < interventionEvents.length - 1 ? `1px solid ${c.border}` : 'none',
                  }}>
                    <span style={{ fontSize: 12, color: c.slate, fontWeight: 600, flex: 1 }}>{title}</span>
                    <span style={{ fontSize: 12, color: c.textSoft }}>{relDate(ev.event_date)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Owner-reported changes (pet_change_logs, "Log Change" button,
            2026-07-24) — kept as its own section rather than merged into
            Recent Events above: this is an owner's unprompted, unverified
            perception of a domain-level change (energy, appetite, etc.),
            not a discrete clinical event, and the section title says so
            explicitly so it doesn't read as clinically confirmed. Purely
            informational — never fed into severity/alert computation. */}
        {petChangeLogs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('patientDetail.recentChanges')}</div>
            <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${c.border}` }}>
              {petChangeLogs.map((chg, i) => {
                // Same explicit-membership-check pattern as interventionEvents
                // above, not i18next's own defaultValue mechanism — matches
                // existing convention in this file.
                const label = CHANGE_DOMAIN_KEYS.has(chg.domain)
                  ? t(`changeDomain.${chg.domain}`)
                  : chg.domain?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || t('changeDomain.other');
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', padding: '9px 12px',
                    borderBottom: i < petChangeLogs.length - 1 ? `1px solid ${c.border}` : 'none',
                  }}>
                    <span style={{ fontSize: 12, color: c.slate, fontWeight: 600, flex: 1 }}>{label}</span>
                    <span style={{ fontSize: 12, color: c.textSoft }}>{relDate(chg.log_date)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Check-in history */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('patientDetail.recentCheckIns')}</div>
          {loadingSurveys ? (
            <div style={{ fontSize: 12, color: c.textSoft }}>{t('common.loading')}</div>
          ) : surveysError ? (
            <div role="alert" style={{ border: `1px solid ${c.error}30`, backgroundColor: '#FDECEC', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, color: c.error, marginBottom: 8 }}>{t('patientDetail.checkInsLoadError')}</div>
              <button
                onClick={() => setSurveysRetry(n => n + 1)}
                style={{ padding: '5px 10px', backgroundColor: c.white, color: c.slate, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
              >
                {t('common.retry')}
              </button>
            </div>
          ) : surveys.length === 0 ? (
            <div style={{ fontSize: 12, color: c.textSoft }}>{t('patientDetail.noCompletedCheckIns')}</div>
          ) : (
            <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${c.border}` }}>
              {surveys.map((s, i) => {
                // Release-blocker #1: no raw score/delta digits — a
                // qualitative trend word stands in for both the old
                // clinical_score span and the signed clinical_score_delta
                // span together.
                const rowDirection = s.clinical_score_delta != null ? trendDirectionLabel(s.clinical_score_delta, t) : null;
                const dColor    = s.clinical_score_delta > 0 ? c.green : s.clinical_score_delta < 0 ? c.red : c.textSoft;
                const isOpen    = expandedId === s.id;
                const answers   = answerCache[s.id];
                const isLoading = isOpen && loadingAns && answers === undefined;
                const rowRun    = vnextRuns.get(s.id);
                const rowFindingsLines = buildFindingsNarrative(rowRun?.findings, rowRun?.clusters);
                return (
                  <div key={s.id} style={{ borderBottom: i < surveys.length - 1 ? `1px solid ${c.border}` : 'none' }}>
                    {/* Row header — clickable */}
                    <div
                      onClick={() => handleRowClick(s.id, s.template_id)}
                      onKeyDown={onActivateKeyDown(() => handleRowClick(s.id, s.template_id))}
                      tabIndex={0}
                      role="button"
                      aria-expanded={isOpen}
                      aria-label={t('patientDetail.checkInA11y', { date: relDate(s.submitted_at), trend: rowDirection || t('patientCard.directionUnknown') })}
                      style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', backgroundColor: isOpen ? c.fog : c.white, cursor: 'pointer' }}
                    >
                      <span style={{ fontSize: 12, color: c.textSoft, flex: 1 }}>{relDate(s.submitted_at)}</span>
                      {rowDirection && <span style={{ fontSize: 12, fontWeight: 600, color: dColor }}>{rowDirection}</span>}
                      <span style={{ fontSize: 10, color: c.textSoft, marginLeft: 8, transition: 'transform 0.15s', display: 'inline-block', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                    </div>
                    {/* Owner's optional free-text note — surfaced without needing
                        to expand, since it's something the owner specifically
                        chose to type */}
                    {s.owner_note && (
                      <div style={{ padding: '0 12px 9px', fontSize: 12, color: c.slate, fontStyle: 'italic' }}>
                        "{s.owner_note}"
                      </div>
                    )}
                    {/* This specific check-in's own vNext "why" -- only
                        shown once expanded, alongside the raw answers,
                        since the roster/panel-top narrative above already
                        covers the LATEST check-in. */}
                    {isOpen && rowFindingsLines.length > 0 && (
                      <div style={{ padding: '0 12px 9px' }}>
                        {rowFindingsLines.map((line, li) => (
                          <div key={li} style={{ fontSize: 12, color: c.slate, fontWeight: 500 }}>{line}</div>
                        ))}
                      </div>
                    )}
                    {/* Expanded answers */}
                    {isOpen && (
                      <div style={{ backgroundColor: '#f9faf8', borderTop: `1px solid ${c.border}`, padding: '8px 12px' }}>
                        {isLoading ? (
                          <div style={{ fontSize: 12, color: c.textSoft, padding: '4px 0' }}>{t('patientDetail.loadingResponses')}</div>
                        ) : answerLoadErrors.has(s.id) ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => loadAnswers(s.id, s.template_id)}
                            onKeyDown={onActivateKeyDown(() => loadAnswers(s.id, s.template_id))}
                            style={{ fontSize: 12, color: c.error, padding: '4px 0', cursor: 'pointer' }}
                          >
                            {t('patientDetail.responsesLoadError')}
                          </div>
                        ) : !answers?.length ? (
                          <div style={{ fontSize: 12, color: c.textSoft, padding: '4px 0' }}>{t('patientDetail.noResponsesRecorded')}</div>
                        ) : (
                          <>
                          {answers.some(a => a.contribution) && (
                            <div style={{ fontSize: 10, color: c.textSoft, padding: '2px 8px 7px', lineHeight: 1.35 }}>
                              {t('patientDetail.contributionExplanation')}
                            </div>
                          )}
                          {answers.map((a, ai) => {
                            const contributionColor = a.contribution === 'positive' ? c.greenText : a.contribution === 'negative' ? c.red : null;
                            const contributionBorder = a.contribution === 'positive' ? c.green : a.contribution === 'negative' ? c.red : null;
                            const contributionBg = a.contribution === 'positive' ? `${c.green}12` : a.contribution === 'negative' ? `${c.red}10` : null;
                            return (
                            <div key={ai} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                              padding: '5px 8px', borderRadius: 6, marginBottom: 2,
                              backgroundColor: contributionBg || (a.score_weight === 0 ? 'rgba(194,169,138,0.12)' : 'transparent'),
                              borderLeft: contributionBorder ? `3px solid ${contributionBorder}` : '3px solid transparent',
                            }}>
                              <span style={{ fontSize: 12, color: c.textSoft, flex: 1, paddingRight: 12, lineHeight: 1.4 }}>{a.question_text || a.question_key}</span>
                              <span style={{ flexShrink: 0, textAlign: 'right', maxWidth: '48%' }}>
                                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: c.slate }}>
                                  {formatAnswerValue(a.answer_value, a.answer_type)}
                                </span>
                                {a.contribution && (
                                  <span style={{ display: 'block', marginTop: 3, fontSize: 10, fontWeight: 700, color: contributionColor }}>
                                    {a.contribution === 'positive' ? `↑ ${t('patientDetail.positiveContribution')}` : `↓ ${t('patientDetail.negativeContribution')}`}
                                  </span>
                                )}
                              </span>
                            </div>
                            );
                          })}
                          </>
                        )}
                        {/* Medication snapshot at time of this check-in —
                            written by SurveyScreen.js specifically so a
                            score change can be correlated with a medication
                            change, previously never surfaced anywhere. */}
                        {!isLoading && medSnapshotCache[s.id]?.length > 0 && (
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${c.border}` }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                              {t('patientDetail.medsAtCheckIn')}
                            </div>
                            {medSnapshotCache[s.id].map((m, mi) => (
                              <div key={mi} style={{ fontSize: 12, color: c.slate, padding: '2px 8px' }}>
                                {m.medication_name}
                                {[m.dose && `${m.dose}${m.dose_unit || ''}`, m.frequency].filter(Boolean).length > 0
                                  ? ` — ${[m.dose && `${m.dose}${m.dose_unit || ''}`, m.frequency].filter(Boolean).join(', ')}`
                                  : ''}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Owner contact */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('patientDetail.owner')}</div>
          <div style={{ backgroundColor: c.fog, borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: c.slate }}>{patient.owner?.first_name} {patient.owner?.last_name}</div>
            {patient.owner?.phone && <div style={{ fontSize: 12, color: c.textSoft, marginTop: 2 }}>{formatPhone(patient.owner.phone)}</div>}
            {patient.owner?.email && <div style={{ fontSize: 12, color: c.textSoft }}>{patient.owner.email}</div>}
          </div>
        </div>

      </div>
      {finalizeOpen && (
        <FinalizePatientModal
          patient={patient}
          onConfirm={handleFinalize}
          onCancel={() => { setFinalizeOpen(false); setFinalizeError(''); }}
          loading={finalizing}
          error={finalizeError}
        />
      )}
      {requestSent && (
        <RequestSentModal
          petName={patient.pet.name}
          onClose={() => { setRequestSent(false); onClose(); }}
        />
      )}
      {muteDelinkOpen && (
        <MuteDelinkModal
          petName={patient.pet.name}
          mode={muteDelinkOpen}
          onConfirm={handleMuteDelink}
          onCancel={() => { setMuteDelinkOpen(null); setMuteDelinkError(''); }}
          loading={muteDelinkSaving}
          error={muteDelinkError}
        />
      )}
      </div>
    </div>
  );
}
